import { FileData, toDataUrl } from "./fileUtils";

const DEFAULT_MAX_DIMENSION = 1600;
const DEFAULT_QUALITY = 0.85;
const COMPRESSIBLE_MIME_TYPES = new Set(["image/jpeg", "image/png", "image/webp", "image/heic", "image/heif"]);

export type CompressImageOptions = {
  maxDimension?: number;
  quality?: number;
};

export async function compressImage(image: FileData, options: CompressImageOptions = {}): Promise<FileData> {
  if (!COMPRESSIBLE_MIME_TYPES.has(image.mimeType)) {
    return image;
  }
  const maxDimension = options.maxDimension ?? DEFAULT_MAX_DIMENSION;
  const quality = options.quality ?? DEFAULT_QUALITY;
  try {
    const bitmap = await loadImageBitmap(image);
    const { width, height } = scaledDimensions(bitmap.width, bitmap.height, maxDimension);
    const canvas = createCanvas(width, height);
    const ctx = canvas.getContext("2d");
    if (!ctx) return image;
    ctx.drawImage(bitmap, 0, 0, width, height);
    const blob = await canvasToBlob(canvas, "image/jpeg", quality);
    if (!blob) return image;
    const compressedBase64 = await blobToBase64(blob);
    if (!compressedBase64) return image;
    const compressed: FileData = {
      name: replaceExtension(image.name, "jpg"),
      mimeType: "image/jpeg",
      base64: compressedBase64
    };
    return compressed.base64.length < image.base64.length ? compressed : image;
  } catch {
    return image;
  }
}

export function scaledDimensions(
  srcWidth: number,
  srcHeight: number,
  maxDimension: number
): { width: number; height: number } {
  if (srcWidth <= 0 || srcHeight <= 0) return { width: srcWidth, height: srcHeight };
  const longest = Math.max(srcWidth, srcHeight);
  if (longest <= maxDimension) return { width: srcWidth, height: srcHeight };
  const ratio = maxDimension / longest;
  return { width: Math.round(srcWidth * ratio), height: Math.round(srcHeight * ratio) };
}

async function loadImageBitmap(image: FileData): Promise<HTMLImageElement | ImageBitmap> {
  if (typeof createImageBitmap === "function") {
    const response = await fetch(toDataUrl(image));
    const blob = await response.blob();
    return await createImageBitmap(blob);
  }
  return await loadHtmlImage(toDataUrl(image));
}

function loadHtmlImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("Failed to load image"));
    img.src = src;
  });
}

function createCanvas(width: number, height: number): HTMLCanvasElement {
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  return canvas;
}

function canvasToBlob(canvas: HTMLCanvasElement, mimeType: string, quality: number): Promise<Blob | null> {
  return new Promise(resolve => canvas.toBlob(resolve, mimeType, quality));
}

function blobToBase64(blob: Blob): Promise<string | null> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result;
      if (typeof result !== "string") {
        resolve(null);
        return;
      }
      const base64 = result.split(",")[1] ?? null;
      resolve(base64);
    };
    reader.onerror = () => reject(reader.error ?? new Error("Failed to read blob"));
    reader.readAsDataURL(blob);
  });
}

function replaceExtension(name: string, newExtension: string): string {
  const dotIndex = name.lastIndexOf(".");
  if (dotIndex <= 0) return `${name}.${newExtension}`;
  return `${name.slice(0, dotIndex)}.${newExtension}`;
}
